<!-- eslint-disable max-lines -->
<!--TODO(PRODUCT-17632): refactor and make this file smaller-->
<template>
    <PageWrapper
        :loading="rpChangeCount > 0"
        :description="selectedEntity ? undefined : description"
        :title="selectedEntity ? undefined : 'Entities'"
    >
        <template v-if="!loadingCategoryStatistics && categoryStatistics.total === 0">
            <EntitiesEmptyState
                :loading="loadingCustomProperties || loadingRequirementTypes"
                :show-contract-upload-button="canUploadContract"
                @download-sample-csv="downloadSampleCsv"
                @open-add-individual-entity-modal="openAddIndividualEntityModal"
                @open-bulk-import-entities-modal="openBulkImportEntitiesModal"
                @open-upload-contract-modal="openUploadContractModal"
            />
        </template>
        <template v-else-if="selectedEntity">
            <EntityDetails
                :currently-updated-entities-ids="entityLoadingIdList"
                :requirement-details-list="coverageDetailsListStatus.list"
                :requirement-details-loaded="requirementDetailsLoaded"
                :collateral-entities="collateralEntitiesStatus.list"
                :entity-risk-profiles="entityRiskProfilesStatus.list"
                :entity="selectedEntity"
                :custom-properties="sortedCustomProperties"
                :is-loading-submission-link="loadingSubmissionLink"
                :submission-link-per-entity-id="submissionLinkPerEntityId"
                :loading="
                    loadingEntityDetails
                        || loadingEntityRiskProfiles
                        || loadingCredentialTypes
                        || loadingCollateralEntities
                "
                :is-collateral-enabled="isCollateralEnabled"
                :requirement-models="requirementModelsForEntity(selectedEntity)"
                :external-alert-config="entityDetailsAlertConfig"
                :collateral-custom-property-key="collateralCustomPropertyKey"
                :enum-labels="categorizedEnumLabels"
                :credential-types="credentialTypes"
                :validate-producer-emails="validateProducerEmails"
                @close="closeEntityDetails"
                @go-to-criterion="goToCriterion"
                @update-entities="updateEntities"
                @rename-entity="renameEntity"
                @set-entities-active-state="setEntitiesActiveState"
                @set-entities-pause-state="setEntitiesPauseState"
                @send-document-request="onSendDocumentRequestFromEntityDetails"
                @copy-submission-link="copySubmissionLink"
                @load-entity-requests="onActionsButtonClicked"
                @evaluation-updated="onEvaluationUpdated"
                @upload-entity-document="uploadEntityDocument"
                @add-note="addNote"
            />
        </template>
        <template v-else>
            <DashboardAlertList :configs="alertConfigList" />
            <CategoryTiles
                v-if="entityStatisticsStatus.statistics"
                :value="categoryStatistics"
                :selected="selectedCategory"
                :disabled="entitiesStatus.loading"
                @input="onSelectCategory"
            />
            <EntitiesTable
                :entities="entitiesStatus.list"
                :finished="entitiesStatus.finished"
                :loading="entitiesStatus.loading"
                :exporting="isExporting"
                :sort="sorting"
                :displayed-custom-properties="customPropertiesToDisplay"
                :currently-updated-entities-ids="entityLoadingIdList"
                :is-performing-bulk-action="patchEntitiesStatus.isBulk"
                :is-loading-submission-link="loadingSubmissionLink"
                :submission-link-per-entity-id="submissionLinkPerEntityId"
                :show-contract-upload-button="canUploadContract"
                @copy-submission-link="copySubmissionLink"
                @load-entity-requests="onActionsButtonClicked"
                @select-entity="showEntityDetails"
                @sort="changeSorting"
                @export="exportEntities"
                @open-add-individual-entity-modal="openAddIndividualEntityModal"
                @open-bulk-import-entities-modal="openBulkImportEntitiesModal"
                @open-upload-contract-modal="openUploadContractModal"
                @set-entities-active-state="setEntitiesActiveState"
                @set-entities-pause-state="setEntitiesPauseState"
                @clear-filters="clearFilters"
                @send-document-request="onSendDocumentRequestFromEntitiesTable"
                @grant-exception-to-entity="grantException"
                @remove-exceptions="removeExceptions"
                @view-submission-history="onViewSubmissionHistory"
                @upload-entity-document="uploadEntityDocument"
                @add-note="addNote"
                @scroll-end="loadNextEntities"
            >
                <template #toolbar>
                    <RbacDisplayGuard :features="[ RbacFeature.dashboardSearchAndFilter]">
                        <EntitySearchAndFilter
                            :filters="filters"
                            :risk-profiles="riskProfileStatus.list"
                            :effective-risk-profiles="effectiveRiskProfilesStatus.list"
                            :custom-properties="customPropertiesOther"
                            :disabled="entitiesStatus.loading"
                            :collateral-custom-property-key="collateralCustomPropertyKey"
                            :custom-property-collaterals="customPropertyCollaterals"
                            @change="changeFilters"
                        />
                    </RbacDisplayGuard>
                    <RbacDisplayGuard :features="[ RbacFeature.dashboardSearchAndFilter]">
                        <FiltersChipList
                            v-if="hasAnyDisplayableFilters"
                            :filters="filters"
                            :custom-properties="customPropertiesOther"
                            :disabled="entitiesStatus.loading"
                            :collateral-custom-property-key="collateralCustomPropertyKey"
                            @close-filters="closeFilters"
                        />
                    </RbacDisplayGuard>
                </template>
            </EntitiesTable>
        </template>
        <UploadContractModal
            :open="canUploadContract && isUploadContractModalOpen"
            :submitting="isUploadingContracts"
            @close="closeUploadContractModal"
            @upload="uploadContract"
        />
    </PageWrapper>
</template>

<script lang="ts">
/* eslint-disable max-lines */
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { isEmpty, noop, omit, pick, pickBy, uniq } from 'lodash';

    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import {
        AlertConfig, AlertType,
    } from '@evidentid/dashboard-commons/components/Alert/types';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { usePersistingErrorStore } from '@evidentid/dashboard-commons/modules/persisting-error/store';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        hasSufficientFeatures,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/has-sufficient-features/hasSuffiencientFeatures';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { EntityPatch, PatchOperationType } from '@evidentid/rpweb-api-client';
    import {
        TprmCredentialType, TprmRequirementDefinition,
    } from '@evidentid/tprm-portal-lib/models/common';
    import {
        CustomProperty, Entity, EntityInput, UiConfig,
    } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { EntityStatistics } from '@evidentid/tprm-portal-lib/models/dashboard/EntityStatistics.model';
    import { EntityRiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning';
    import {
        CollateralEntity, EntityRequirementDetails, TprmRequirementModel, TprmRequirementType,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import {
        Sorting, SortingDirection, deserializeSorting, serializeMultiSorting, serializeSorting,
    } from '@evidentid/universal-framework/sorting';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { useContractExtractionStore } from '@/modules/contract-extraction/store';
    import DashboardAlertList from '@/modules/dashboard/components/DashboardAlertList/DashboardAlertList.vue';
    import EntitiesEmptyState from '@/modules/dashboard/components/EntitiesEmptyState/EntitiesEmptyState.vue';
    import EntitiesTable from '@/modules/dashboard/components/EntitiesTable/EntitiesTable.vue';
    import UploadContractModal from '@/modules/dashboard/components/UploadContractModal/UploadContractModal.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { DashboardAlertConfig } from '@/modules/dashboard/types';
    import { describeImportFailures } from '@/modules/dashboard/utils/describeImportFailures';
    import { useContractUploadStore } from '@/modules/dashboard/vuex/contractUpload.store';
    import { DashboardState, RpConfigStatus, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { useDashboardConfigurationStore } from '@/modules/dashboard-configuration/store';
    import {
        EntityEffectiveRiskProfileStatus, RequirementModelsStatus, RiskProfilesStatus,
        useDecisioningCriteriaStore,
    } from '@/modules/decisioning-criteria/store';
    import { ActionableAlertConfig } from '@/modules/entity-details/components/ActionableAlert/types';
    import EntityDetails from '@/modules/entity-details/components/EntityDetails/EntityDetails.vue';
    import { ENTITY_DETAILS_TABS } from '@/modules/entity-details/constants/entityDetailsTabs.constants';
    import { SUBMISSION_HISTORY_ACTIVITY_TYPES } from '@/modules/entity-details/constants/submissionHistoryActivityTypes.constants';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import { createEntityLevelException } from '@/modules/entity-details/utils/createException';
    import FiltersChipList from '@/modules/entity-filtering/components/ActiveFiltersChipList/FiltersChipList.vue';
    import CategoryTiles from '@/modules/entity-filtering/components/CategoryTiles/CategoryTiles.vue';
    import EntitySearchAndFilter
        from '@/modules/entity-filtering/components/EntitySearchAndFilter/EntitySearchAndFilter.vue';
    import { EntityFilterCategory, EntityFilters } from '@/modules/entity-filtering/types';
    import {
        categoryFilterMap, getCategoryByFilters, getCustomPropertyFilterKeys, getEntityCollateralFieldFiltersKeys,
        getEntityStandardFilterKeys, parseFiltersForApi,
    } from '@/modules/entity-filtering/utils/entityFilterUtils';
    import {
        BatchAddEntitiesStatus, BatchUpsertEntitiesStatus, createEmptyUpsertEntitiesStatus, PatchEntitiesStatus,
        useEntityManagementStore,
    } from '@/modules/entity-management/store';
    import { downloadSampleCsv } from '@/modules/entity-management/utils/csv-sample';
    import { SendRequestStatus, useEntityRequestStore } from '@/modules/entity-request/store';
    import { SendDocumentRequestResult } from '@/modules/entity-request/types';
    import { useExportEntitiesStore } from '@/modules/export-entities/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';
    import {
        getRequirementModelsWithCountryLabel,
    } from '@/utils/get-requirement-models-with-country-label/getRequirementModelsWithCountryLabel';
    import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';
    import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
    import { isEquipmentCustomProperty } from '@/utils/isEquipmentCustomProperty';

    type AlertConfigName =
        'alertConfig'
        | 'additionalAlertConfig'
        | 'entityDetailsAlertConfig'
        | 'collectEvidenceAlert'
        | 'errorAlertConfig';
    type AlertTimeoutName =
        'alertConfigTimeout'
        | 'additionalAlertConfigTimeout'
        | 'entityDetailsAlertTimeout'
        | 'collectEvidenceAlertTimeout'
        | 'errorAlertTimeout';
    type FinishDocumentRequestCallbackFnc = (...args: unknown[]) => Promise<unknown>;

    @Component({
        components: {
            RbacDisplayGuard, DashboardAlertList, PageWrapper, EntitiesTable, Alert, FontAwesomeIcon,
            EntitiesEmptyState, CategoryTiles, EntitySearchAndFilter, FiltersChipList, EntityDetails, LogoLoader,
            UploadContractModal,
        },
    })
    export default class InsuranceDashboard extends Vue {
        private get entityRequestStore() {
            return useEntityRequestStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private readonly defaultSorting = { column: 'displayName', direction: SortingDirection.asc };

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        @Watch('rpChangeCount', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.rpChangeCount > 0);
        }
        private lastRpName!: string;
        private alertConfig: AlertConfig | null = null;
        private alertConfigTimeout: any = null;
        private additionalAlertConfig: AlertConfig | null = null;
        private additionalAlertConfigTimeout: any = null;
        private entityDetailsAlertConfig: ActionableAlertConfig | null = null;
        private entityDetailsAlertTimeout: any = null;
        private collectEvidenceAlert: AlertConfig | null = null;
        private collectEvidenceAlertTimeout: any = null;
        private errorAlertConfig: DashboardAlertConfig | null = null;
        private errorAlertTimeout: any = null;
        private rpChangeCount: number = 0;
        private loadingSubmissionLink: boolean = false;
        private isUploadContractModalOpen: boolean = false;
        private RbacFeature = RbacFeature;
        private description = 'View and manage all third parties in one place. Track compliance status, review assigned requirements, and use filters to quickly find entities by status or custom properties.';

        @Watch('$rp.current', { immediate: true })
        private async handleRpChange(rpName: string, prevRpName: string) {
            // Save information about the last resource, which should be cleared during destroy
            this.lastRpName = rpName;
            ++this.rpChangeCount;

            if (prevRpName) {
                useDashboardStore().clearClientRequirementTypeList({ rpName: prevRpName });
                useDashboardStore().clearEntitiesList({ rpName: prevRpName });
                useDashboardStore().clearEntityStatistics({ rpName: prevRpName });
                useDashboardStore().clearSubmissionLinks({ rpName: prevRpName });
                useDashboardStore().clearRpConfig({ rpName: prevRpName });
                useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName: prevRpName });
                useDecisioningCriteriaStore().clearRpEntityRiskProfiles({ rpName: prevRpName });
                useDecisioningCriteriaStore().clearRiskProfiles({ rpName: prevRpName });
                useDecisioningCriteriaStore().clearEffectiveRiskProfilesStatus({ rpName: prevRpName });
                useDecisioningCriteriaStore().clearRequirementModelsStatus({ rpName: prevRpName });
                useContractUploadStore().clearUploadContractStatus({ rpName: prevRpName });
                this.closeEntityDetails();
            }
            // PRODUCT-26110 Phase 2B2: `$rp.current` is genuinely reactive again (see
            // createRpController) and legitimately passes through null while the app sits
            // on rp-less routes (auth/error redirects). Loading with an empty rp fired
            // `/rps//...` requests whose 404s the persistent-error handler treats as
            // fatal. Skip the load barrage until a real rp arrives.
            if (!rpName) {
                return;
            }
            // reloadEntity checks for filter which is relying on customProperties to be loaded first
            await this.loadConfig();
            await Promise.all([
                this.loadClientRequirementTypes(),
                this.reloadEntities(),
                this.loadEntityStatistics(),
                this.loadRiskProfiles(),
                this.loadEffectiveRiskProfiles(),
                this.loadRequirementModels(),
            ]);
            const entityId = this.$route.query?.entity || null;
            if (entityId && typeof entityId === 'string') {
                await useDashboardStore().loadEntity({ rpName: this.rpName, id: entityId });
                await this.loadEntityCoverageData({ rpName: this.rpName, entityId });
            }
            --this.rpChangeCount;
        }

        @Watch('$route.query')
        private handleQueryChange() {
            const entityId = this.$route.query?.entity || null;
            void this.reloadEntityOrCoverageData(entityId as string | null);
        }

        private async reloadEntityOrCoverageData(entityId: string | null): Promise<void> {
            if (!entityId) {
                await this.reloadEntities();
                return;
            }
            // `selectedEntity` resolves against the loaded list, so an id from outside it
            // -- pasted or linked -- has to be fetched first.
            if (!this.entitiesStatus.list.some((entity) => entity.id === entityId)) {
                try {
                    await useDashboardStore().loadEntity({ rpName: this.rpName, id: entityId });
                } catch {
                    // `loadEntity` re-throws a missing entity; every other failure has already
                    // raised its own error by the time it gets here.
                    this.configEntityNotFoundAlert();
                    return;
                }
            }
            await this.loadEntityCoverageData({ rpName: this.rpName, entityId });
        }

        private configEntityNotFoundAlert(): void {
            this.errorAlertConfig = {
                type: 'danger',
                title: 'That entity could not be found.',
            };
            this.errorAlertTimeout = setTimeout(() => {
                this.errorAlertConfig = null;
            }, 5000);
        }

        private async loadEntityCoverageData(
            payload: { rpName: string, entityId: string },
        ): Promise<void[]> {
            const { rpName, entityId } = payload;
            return Promise.all([
                useEntityDetailsStore().loadRequirementDetailsList({ rpName, entityId }),
                useEntityDetailsStore().loadCollateralEntities({ rpName, entityId }),
                useEntityDetailsStore().loadOpenVerification({ rpName, entityId }),
                this.loadEntityRiskProfiles(),
            ]);
        }

        private getLoadEntitiesParams() {
            const apiFilters = pickBy(this.filters, Boolean);
            const modifiedSort = (this.sorting && this.sorting.column === 'complianceStatus')
                ? serializeMultiSorting([ { column: 'active', direction: SortingDirection.desc }, this.sorting ])
                : serializeSorting(this.sorting);
            return {
                rpName: this.rpName,
                sort: modifiedSort || null,
                filters: parseFiltersForApi(
                    apiFilters,
                    this.customPropertiesOther,
                    this.collateralCustomPropertyKey,
                    this.customPropertyCollaterals,
                ),
            };
        }

        private async loadNextEntities() {
            if (!this.entitiesStatus.loading && !this.entitiesStatus.finished) {
                await useDashboardStore().loadNextEntities(this.getLoadEntitiesParams());
            }
        }

        private async loadEntities() {
            await useDashboardStore().loadEntities(this.getLoadEntitiesParams());
        }

        private async loadEntityStatistics() {
            if (this.entityStatisticsStatus.status !== OperationStatus.loading) {
                await useDashboardStore().loadEntityStatistics({ rpName: this.rpName });
            }
        }

        private async loadClientRequirementTypes() {
            if (!this.loadingRequirementTypes) {
                await useDashboardStore().loadClientRequirementTypes({ rpName: this.rpName });
            }
        }

        private async loadConfig() {
            if (!this.loadingDisplayConfig) {
                await useDashboardStore().loadRpConfig({ rpName: this.rpName });
            }
        }

        private async loadRiskProfiles() {
            if (this.riskProfileStatus.status !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().loadRiskProfiles({ rpName: this.rpName });
            }
        }

        private async loadEffectiveRiskProfiles() {
            if (this.effectiveRiskProfilesStatus.status !== OperationStatus.loading) {
                await useDecisioningCriteriaStore().loadEffectiveRiskProfiles({ rpName: this.rpName });
            }
        }

        private async loadRequirementModels() {
            if (this.requirementModelsStatus.status !== OperationStatus.loading) {
                useDecisioningCriteriaStore().loadRequirementModels({ rpName: this.rpName });
            }
        }

        private async loadEntityRiskProfiles() {
            if (!this.loadingEntityRiskProfiles && this.selectedEntity) {
                await useDecisioningCriteriaStore().loadEntityRiskProfiles({
                    rpName: this.rpName,
                    entityId: this.selectedEntity.id,
                });
            }
        }

        private get loadingCategoryStatistics(): boolean {
            return this.entityStatisticsStatus.status === OperationStatus.loading;
        }

        private get loadingCustomProperties(): boolean {
            return this.customPropertiesStatus.status === OperationStatus.loading;
        }

        private get loadingRequirementTypes(): boolean {
            return this.clientRequirementTypesStatus.status === OperationStatus.loading;
        }

        private get loadingDisplayConfig(): boolean {
            return this.customPropertiesDisplayStatus.status === OperationStatus.loading;
        }

        private get loadingEntityDetails(): boolean {
            return this.coverageDetailsListStatus.status === OperationStatus.loading;
        }

        /** A failed load leaves the list empty without being loading, so consumers that read
         * meaning into an empty list need to know the difference. */
        private get requirementDetailsLoaded(): boolean {
            return this.coverageDetailsListStatus.status === OperationStatus.success;
        }

        private get loadingCollateralEntities(): boolean {
            return this.collateralEntitiesStatus.status === OperationStatus.loading;
        }

        private get loadingEntityRiskProfiles(): boolean {
            return this.entityRiskProfilesStatus.status === OperationStatus.loading;
        }

        private get rpName(): string {
            return this.$rp?.current || '';
        }

        private get entitiesStatus() {
            return (useDashboardStore().$state as DashboardState).entities[this.rpName] || {
                count: Infinity,
                finished: false,
                loading: false,
                list: [],
            };
        }

        private get customPropertiesStatus(): { status: OperationStatus, list: CustomProperty[] } {
            const config = useDashboardStore().rpConfig[this.rpName];
            return {
                status: config?.status ?? OperationStatus.uninitialized,
                list: config?.customProperties ?? [],
            };
        }

        private get sortedCustomProperties(): CustomProperty[] {
            return getSortedCustomProperties(
                this.customPropertiesStatus.list,
                this.customPropertiesDisplayStatus.data.customPropertiesOrder,
            );
        }

        private get customPropertiesToDisplay(): CustomProperty[] {
            const { entitiesTableCustomProperties } = this.customPropertiesDisplayStatus.data;
            return entitiesTableCustomProperties
                .map((key) => this.customPropertiesStatus.list.find((p) => p.key === key))
                .filter((property): property is CustomProperty =>
                    Boolean(property) && !isEquipmentCustomProperty(property as CustomProperty));
        }

        private get customPropertiesOther(): CustomProperty[] {
            return this.sortedCustomProperties.filter((customProperty) =>
                !isCollateralCustomProperty(customProperty) && !isEquipmentCustomProperty(customProperty),
            );
        }

        private get customPropertyCollaterals(): CustomProperty | null {
            return this.customPropertiesStatus.list.find(isCollateralCustomProperty) || null;
        }

        private get collateralCustomPropertyKey(): string {
            return this.customPropertyCollaterals?.key || '';
        }

        private get clientRequirementTypesStatus(): { status: OperationStatus, list: string[] } {
            return useDashboardStore().clientRequirementTypes[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get requirementDefinitions(): Record<string, TprmRequirementDefinition> {
            return useDashboardStore().tprmRequirementsDefinitions || {};
        }

        private get customPropertiesDisplayStatus(): {
            status: OperationStatus;
            data: UiConfig;
        } {
            const config = useDashboardStore().rpConfig[this.rpName];
            return {
                status: config?.status ?? OperationStatus.uninitialized,
                data: {
                    customPropertiesOrder: config?.customPropertiesOrder ?? [],
                    entitiesTableCustomProperties: config?.entitiesTableCustomProperties ?? [],
                },
            };
        }

        private get entityStatisticsStatus(): { status: OperationStatus, statistics: EntityStatistics } {
            return useDashboardStore().entityStatistics[this.rpName] || {
                status: OperationStatus.uninitialized,
                statistics: {
                    compliance: {
                        forcePlaced: 0,
                        compliant: 0,
                        nonCompliant: 0,
                        pending: 0,
                        new: 0,
                    },
                    expiration: {
                        within30Days: 0,
                    },
                },
            };
        }

        private get categoryStatistics(): Record<EntityFilterCategory | 'total', number> {
            const { compliance, expiration } = this.entityStatisticsStatus.statistics;
            return {
                [EntityFilterCategory.compliantAndNotExpiringSoon]: compliance.compliant - expiration.within30Days,
                [EntityFilterCategory.nonCompliant]: compliance.nonCompliant,
                [EntityFilterCategory.pendingAndNew]: compliance.pending + compliance.new,
                [EntityFilterCategory.compliantAndExpiringSoon]: expiration.within30Days,
                total: Object.entries(compliance).reduce((acc, [ , value ]) => acc + value, 0),
            };
        }

        private get exportStatus(): OperationStatus {
            return useExportEntitiesStore().exportStatus[this.rpName] ?? OperationStatus.uninitialized;
        }

        private get isExporting(): boolean {
            return this.exportStatus === OperationStatus.loading;
        }

        private get contractUploadStatus(): OperationStatus {
            return useContractUploadStore().uploadContractStatus?.[this.rpName]
                || OperationStatus.uninitialized;
        }

        private get isUploadingContracts(): boolean {
            return this.contractUploadStatus === OperationStatus.loading;
        }

        private get sorting(): Sorting {
            return deserializeSorting(this.$route.query.sort) || this.defaultSorting;
        }

        private get filters(): EntityFilters {
            const standardFilters = pick(this.$route.query, getEntityStandardFilterKeys());
            let customPropertyFilters = {};
            let collateralFieldFilters = {};

            if (this.customPropertiesStatus.status === OperationStatus.success) {
                customPropertyFilters = pick(
                    this.$route.query,
                    getCustomPropertyFilterKeys(this.customPropertiesOther),
                ) as Record<string, string>;
                collateralFieldFilters = pick(
                    this.$route.query,
                    getEntityCollateralFieldFiltersKeys(this.collateralCustomPropertyKey),
                ) as Record<string, string>;
            }

            const filters = { ...standardFilters };

            if (!isEmpty(customPropertyFilters)) {
                Object.assign(filters, { customPropertyFilters });
            }

            if (!isEmpty(collateralFieldFilters)) {
                Object.assign(filters, { collateralFieldFilters });
            }

            return filters;
        }

        private get hasAnyDisplayableFilters(): boolean {
            const { search, ...filters } = this.filters;
            return Object.keys(filters).length > 0;
        }

        private get selectedCategory(): EntityFilterCategory[] {
            const category = getCategoryByFilters(this.filters);
            return category ? [ category ] : [];
        }

        private get addEntitiesStatus(): BatchAddEntitiesStatus {
            return useEntityManagementStore().addEntitiesStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                count: 0,
                successCount: 0,
                successes: [],
                failures: [],
            };
        }

        private get upsertEntitiesStatus(): BatchUpsertEntitiesStatus {
            return useEntityManagementStore().upsertEntitiesStatus[this.rpName]
                || createEmptyUpsertEntitiesStatus();
        }

        private get patchEntitiesStatus(): PatchEntitiesStatus {
            return useEntityManagementStore().patchEntitiesStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                successCount: 0,
                failureCount: 0,
                totalCount: 0,
                successes: [],
                failures: [],
                currentlyUpdated: [],
                isBulk: false,
            };
        }

        private get sendRequestStatus(): SendRequestStatus {
            return this.entityRequestStore.sendDocumentRequestStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                entityIds: [],
                count: 0,
                successCount: 0,
                failureCount: 0,
                successes: [],
                failures: [],
            };
        }

        private get coverageDetailsListStatus(): { status: OperationStatus, list: EntityRequirementDetails[] } {
            return useEntityDetailsStore().requirementDetailsListStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get collateralEntitiesStatus(): { status: OperationStatus, list: CollateralEntity[] } {
            return useEntityDetailsStore().collateralEntitiesStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get effectiveRiskProfilesStatus(): EntityEffectiveRiskProfileStatus {
            return useDecisioningCriteriaStore().effectiveRiskProfileStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get requirementModelsStatus(): RequirementModelsStatus {
            return useDecisioningCriteriaStore().requirementModelsStatus[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get entityRiskProfilesStatus(): {
            status: OperationStatus;
            list: EntityRiskProfile[];
        } {
            const riskProfilesPerRp = useDecisioningCriteriaStore().entityRiskProfiles[this.rpName];
            const riskProfiles = riskProfilesPerRp && this.selectedEntity
                ? riskProfilesPerRp[this.selectedEntity.id]
                : null;
            return riskProfiles || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get grantExceptionStatus(): OperationStatus {
            return useEntityDetailsStore().grantExceptionStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get removeExceptionStatus(): OperationStatus {
            return useEntityDetailsStore().removeExceptionStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get entitySubmissionLinksStatus():
            Record<string, { status: OperationStatus, submissionLink: string }> {
            return (useDashboardStore().$state as DashboardState).submissionLinks[this.rpName] || {};
        }

        private get submissionLinkPerEntityId(): Record<string, string> {
            return Object.entries(this.entitySubmissionLinksStatus)
                .reduce((acc, [ entityId, linkStatus ]) => ({ ...acc, [entityId]: linkStatus.submissionLink }), {});
        }

        private get riskProfileStatus(): RiskProfilesStatus {
            return useDecisioningCriteriaStore().riskProfiles[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get isCollateralEnabled(): boolean {
            return this.$rp.currentDetails?.collateralsEnabled || false;
        }

        private get isContractExtractionEnabled(): boolean {
            return this.$rp.currentDetails?.contractExtractionEnabled || false;
        }

        private get canUploadContract(): boolean {
            return this.isContractExtractionEnabled && hasSufficientFeatures(
                [ RbacFeature.dashboardUploadContract ],
                useUserStore().currentRpRbacFeatures,
                true,
            );
        }

        private get alertConfigList(): DashboardAlertConfig[] {
            return [ this.alertConfig, this.additionalAlertConfig, this.collectEvidenceAlert, this.errorAlertConfig ]
                .filter(Boolean) as DashboardAlertConfig[];
        }

        private get categorizedEnumLabels(): CategorizedEnumLabels {
            return useDashboardStore().categorizedEnumLabels;
        }

        private changeSorting(sort: Sorting): void {
            this.$router.push({
                ...this.$route,
                query: { ...this.$route.query, sort: serializeSorting(sort) },
            } as any);
        }

        private async exportEntities(): Promise<void> {
            await useExportEntitiesStore().exportEntities({ rpName: this.rpName });
            if (this.exportStatus === OperationStatus.error) {
                await useSnackbarStore().displaySnackbar({
                    message: 'Sorry, something went wrong during export. Please try again later.',
                    permanent: true,
                    success: false,
                });
            }
        }

        // Same worksheet the bulk import modal offers, so it has to follow the same rule: no
        // Entity ID column while contactEmails still address entities (PRODUCT-26313). The rule
        // comes from the RP config, and a worksheet built on a guess is one the import then
        // rejects - pointing the client back at this very download - so the config is loaded
        // first and the download is refused outright if it cannot be read.
        private async downloadSampleCsv(): Promise<void> {
            if (this.rpConfigStatus?.status !== OperationStatus.success) {
                await useDashboardStore().loadRpConfig({ rpName: this.rpName });
            }
            const rpConfig = this.rpConfigStatus;
            if (rpConfig?.status !== OperationStatus.success) {
                await useSnackbarStore().displaySnackbar({
                    message: 'Sorry, we could not prepare the worksheet. Please try again in a moment.',
                    permanent: true,
                    success: false,
                });
                return;
            }
            const filteredRequirements = this.clientRequirementTypesStatus.list.filter(
                (type) => !this.requirementDefinitions[type]?.multiInstance,
            );
            downloadSampleCsv(
                this.sortedCustomProperties,
                filteredRequirements,
                rpConfig.contactEmailUniquenessEnabled ?? true,
            );
        }

        private get rpConfigStatus(): RpConfigStatus | undefined {
            return useDashboardStore().rpConfig[this.rpName];
        }

        private get isEntitySelected(): boolean {
            return this.$route.query?.entity !== undefined;
        }

        private get selectedEntity(): Entity | null {
            const entityId = this.$route.query?.entity || null;
            return this.entitiesStatus.list.find((entity) => entity.id === entityId) || null;
        }

        private get entityLoadingIdList(): string[] {
            return uniq([ ...this.patchEntitiesStatus.currentlyUpdated, ...this.sendRequestStatus.entityIds ]);
        }

        private get credentialTypes(): TprmCredentialType[] {
            return useDashboardStore().credentialTypes?.list || [];
        }

        private get loadingCredentialTypes(): boolean {
            return useDashboardStore().credentialTypes?.status === OperationStatus.loading;
        }

        private showEntityDetails(entity: Entity): void {
            this.hideAlert();
            this.$router.push({
                ...this.$route,
                query: { ...this.$route.query, entity: entity.id },
            } as any);
        }

        private closeEntityDetails(): void {
            if (this.isEntitySelected) {
                this.$router.push({
                    ...this.$route,
                    query: {
                        ...omit(this.$route.query, [ 'entity', 'tab' ]),
                    },
                } as any);
            }
        }

        private openAddIndividualEntityModal(): void {
            this.$procedures.execute('openAddIndividualEntityModal', {
                rpName: this.rpName,
            }, (modified: boolean, collectEvidence?: boolean) =>
                this.finishAddEntityProcedure(modified, collectEvidence));
        }

        private openBulkImportEntitiesModal(): void {
            this.$procedures.execute('openBulkImportEntitiesModal', {
                rpName: this.rpName,
            }, (modified: boolean, data: { sampleEntities: EntityInput[], collectEvidence?: boolean }) =>
                this.finishBulkImportEntitiesProcedure(modified, data));
        }

        private openUploadContractModal(): void {
            if (!this.canUploadContract) {
                return;
            }
            this.isUploadContractModalOpen = true;
        }

        private closeUploadContractModal(): void {
            this.isUploadContractModalOpen = false;
        }

        private async uploadContract(files: File[]): Promise<void> {
            if (!Array.isArray(files) || files.length === 0 || this.isUploadingContracts) {
                return;
            }

            const [ file ] = files;
            if (!file) {
                return;
            }

            try {
                await useContractUploadStore().uploadContract({ rpName: this.rpName, file });

                await useSnackbarStore().displaySnackbar({
                    message: 'Contract is uploading and processing. This may take a few moments. You will be notified when finished.',
                    permanent: false,
                    success: true,
                    timeout: 3000,
                });
                this.closeUploadContractModal();
                await useContractExtractionStore().initializePolling({
                    rpName: this.rpName,
                });
            } catch (error) {
                await useSnackbarStore().displaySnackbar(null);
                await usePersistingErrorStore().showError(error);
            }
        }

        private uploadEntityDocument(entity?: Entity): void {
            const resolvedEntity = entity || this.selectedEntity;
            if (!resolvedEntity) {
                return;
            }
            this.$procedures.execute('uploadEntityDocumentProcedure', {
                rpName: this.rpName,
                entityId: resolvedEntity.id,
            });
        }

        // D47: every eligible entity goes through the create-link modal, whatever its verification
        // status, because the email toggle has to be reachable on every path -- which rules out a
        // one-click send.
        private onSendDocumentRequestFromEntityDetails(entities: Entity[]): void {
            // the entities here will only contain up to 1 entity from entity details
            this.openSendDocumentRequestModal(entities, this.onEvaluationUpdated);
        }

        private onSendDocumentRequestFromEntitiesTable(entities: Entity[]): void {
            // keep the original list, including the non-qualified entities, so the bulk
            // confirmation can state how many of the selected entities it will act on.
            this.openSendDocumentRequestModal(entities, this.reloadEntities);
        }

        private openSendDocumentRequestModal(
            entities: Entity[],
            finishCallbackFnc?: FinishDocumentRequestCallbackFnc,
        ): void {
            this.$procedures.execute('openSendDocumentRequestProcedure', {
                rpName: this.rpName,
                entities,
            }, this.finishSendDocumentRequestProcedure(finishCallbackFnc));
        }

        private async reloadEntities(): Promise<void> {
            useDashboardStore().clearEntitiesList({ rpName: this.rpName });
            await this.loadEntities();
        }

        private async onEvaluationUpdated(): Promise<void> {
            const entityId = this.$route.query?.entity || null;
            if (entityId && typeof entityId === 'string') {
                await useDashboardStore().loadEntity({ rpName: this.rpName, id: entityId });
                await this.loadEntityCoverageData({ rpName: this.rpName, entityId });
            }
        }

        private validateProducerEmails(emails: string[]): Promise<string[]> {
            return useEntityManagementStore().validateProducerEmails({
                rpName: this.rpName, emails,
            });
        }

        private async updateEntities(entities: EntityInput[]): Promise<void> {
            await useEntityManagementStore().updateEntities(
                { rpName: this.rpName, entities, updateEntitiesListState: true });
        }

        private async patchEntities(properties: EntityPatch[]): Promise<void> {
            await useEntityManagementStore().patchEntities({
                rpName: this.rpName,
                isBulk: true,
                patch: properties,
            });
        }

        private async renameEntity(patch: EntityPatch): Promise<void> {
            const entity = this.selectedEntity;
            await useEntityManagementStore().patchEntities({
                rpName: this.rpName,
                isBulk: false,
                patch: [ patch ],
            });
            if (entity) {
                this.createEntitiesActionAlert(this.patchEntitiesStatus, [ entity ], 'renamed');
            }
        }

        // PRODUCT-26110 Phase 2B2: prototype method, not an arrow field -- see
        // finishDeactivateEntityProcedure below for why (arrow fields capture a raw
        // `this` without `$store` under vue-facing-decorator). Call site wraps in an arrow.
        private finishAddEntityProcedure(modified: boolean, collectEvidence?: boolean): void {
            if (modified) {
                this.reloadEntities();
                this.loadEntityStatistics();
                this.setAddEntityAlert();
                this.clearEntitiesStatus();
                if (collectEvidence) {
                    this.displayCollectEvidenceMessage();
                }
            }
        }

        // PRODUCT-26110 Phase 2B2: prototype method returning the callback arrow (which
        // now captures the proxy `this`, with `$store`) -- was an arrow field, see
        // finishDeactivateEntityProcedure. Both call sites already invoke it as a method.
        private finishSendDocumentRequestProcedure(callbackFnc?: FinishDocumentRequestCallbackFnc) {
            return async (
                modified: boolean,
                result?: SendDocumentRequestResult,
            ): Promise<void> => {
                // A create-link request has already been confirmed by the procedure's own modal,
                // which shows the link and says whether an email went out. Repeating it here would
                // claim a send on a request whose notification was suppressed.
                if (modified && result && !result.alreadyConfirmed) {
                    const { entities } = result;
                    if (this.sendRequestStatus.status === OperationStatus.success) {
                        if (this.sendRequestStatus.failureCount === 0) {
                            this.showAlert(
                                'success',
                                entities.length === 1
                                    ? `Request to ${entities[0].displayName} was sent.`
                                    : `Requests to ${entities.length} entities were sent.`,
                            );
                        } else if (this.sendRequestStatus.failureCount > 0) {
                            this.showAlert('warning', `${this.sendRequestStatus.successCount} requests was sent,
                                ${this.sendRequestStatus.failureCount} requests have failed.`);
                        }
                    } else {
                        this.showAlert('danger', 'Your request has failed. Please try again.');
                    }
                }
                this.entityRequestStore.clearSendDocumentRequestStatus({ rpName: this.rpName });

                if (callbackFnc) {
                    await callbackFnc();
                }
            };
        }

        // PRODUCT-26110 Phase 2B2: prototype method, not an arrow field (see
        // finishDeactivateEntityProcedure). Call site wraps in an arrow.
        private finishBulkImportEntitiesProcedure(
            modified: boolean,
            data: {
                sampleEntities: EntityInput[];
                collectEvidence?: boolean;
            }): void {
            const procedureData = data || {};
            if (modified) {
                this.reloadEntities();
                this.loadEntityStatistics();
                this.setBulkImportEntitiesAlert(procedureData.sampleEntities);
                this.clearEntitiesStatus();
                if (procedureData.collectEvidence) {
                    this.displayCollectEvidenceMessage();
                }
            }
        }

        // PRODUCT-26110 Phase 2B2: this must be a prototype method, not an arrow class
        // field. Under vue-facing-decorator an arrow field captures the raw class `this`
        // built before the Options-API surface (`$store`, `$procedures`, ...) is wired,
        // so `this.$store` was undefined when the procedure finished later -- deactivating
        // an entity crashed the whole page ("undefined reading 'state'"). As a method it
        // runs with the component proxy `this`; the call site wraps it in an arrow so the
        // detached procedure callback still binds that proxy.
        private async finishDeactivateEntityProcedure(modified: boolean, entities: Entity[]): Promise<void> {
            if (modified) {
                // Awaited for the same reason as the activation twin: reloadEntities empties the
                // list before refilling it, and selectedEntity resolves against that list. With
                // "Show deactivated" on -- the only way the details pane can be open on an entity
                // being deactivated -- the entity stays in the list, so the alert belongs in the
                // pane rather than behind it.
                await this.reloadEntities();
                this.loadEntityStatistics();
                this.createEntitiesActionAlert(this.patchEntitiesStatus, entities, 'deactivated');
                this.clearEntitiesStatus();
            }
        }

        private async finishActivateEntityProcedure(modified: boolean, entities: Entity[]): Promise<void> {
            if (modified) {
                // See finishDeactivateEntityProcedure: awaited so selectedEntity has resolved
                // again by the time the alert picks its target.
                await this.reloadEntities();
                this.loadEntityStatistics();
                this.createEntitiesActionAlert(this.patchEntitiesStatus, entities, 'activated');
                this.clearEntitiesStatus();
            }
        }

        private setAddEntityAlert(): void {
            if (this.addEntitiesStatus.status === OperationStatus.success
                && this.addEntitiesStatus.successCount === 1) {
                this.showAlert('success', 'Connected to new Entity');
            }
        }

        private displayCollectEvidenceMessage(): void {
            this.setupAlertConfig(
                'highWarning',
                'We’ll collect evidence of insurance from those whose policies had already expired',
                'collectEvidenceAlert',
                'collectEvidenceAlertTimeout',
            );
        }

        private setBulkImportEntitiesAlert(sampleEntities: EntityInput[]): void {
            if (this.upsertEntitiesStatus.status === OperationStatus.success) {
                this.configImportSampleAlert(sampleEntities);
                this.configImportEntitiesSuccessAlert();
                if (this.importFailureCount > 0) {
                    this.configImportEntitiesErrorAlert();
                }
            }
        }

        private configImportSampleAlert(sampleEntities: EntityInput[]): void {
            if (sampleEntities && sampleEntities.length) {
                const message = this.upsertEntitiesStatus.totalCount === 0
                    ? 'Attention: All rows of Entity contain sample data. They will be ignored and are not imported'
                    : 'Attention: One or more rows of Entity contain sample data. These rows will be ignored and are not imported';
                this.showAdditionalAlert('highWarning', message);
            }
        }

        // A row that carried an Entity ID updated an existing entity; the rest created one. Both
        // outcomes are reported so the count matches what the user sees in the list.
        //
        // A null updatedCount means rows were matched on contactEmail instead, and nothing says
        // which of them existed already. Reporting them as imports would tell a client who just
        // updated their whole book that they imported it afresh, so the batch is reported as one
        // figure with a verb that covers both (PRODUCT-26313).
        private configImportEntitiesSuccessAlert(): void {
            const { successCount, updatedCount } = this.upsertEntitiesStatus;
            const describe = (count: number, verb: string) =>
                `${count} ${count > 1 ? 'entities' : 'entity'} ${verb}`;
            if (updatedCount === null) {
                if (successCount > 0) {
                    this.showAlert('success', describe(successCount, 'imported or updated'));
                }
                return;
            }
            const importedCount = successCount - updatedCount;
            const parts = [
                importedCount > 0 ? describe(importedCount, 'imported') : '',
                updatedCount > 0 ? describe(updatedCount, 'updated') : '',
            ].filter(Boolean);
            if (parts.length > 0) {
                this.showAlert('success', parts.join(', '));
            }
        }

        // Rows refused one by one and rows lost with the request that carried them both have to
        // be re-imported, so they count the same to the user.
        private get importFailureCount(): number {
            return this.upsertEntitiesStatus.failureCount + this.upsertEntitiesStatus.failedOnRequestCount;
        }

        // Reasons need time to read, and dashboard alerts cannot be closed, so a listing one stays longer.
        private configImportEntitiesErrorAlert(): void {
            const failedCount = this.importFailureCount;
            const entityWord = failedCount > 1 ? 'entities' : 'entity';
            const details = describeImportFailures(this.upsertEntitiesStatus.failures);
            this.errorAlertConfig = {
                type: 'danger',
                title: `${failedCount} ${entityWord} failed to be imported.`,
                details,
            };
            this.errorAlertTimeout = setTimeout(() => {
                this.errorAlertConfig = null;
            }, details.length > 0 ? 30000 : 5000);
        }

        private clearEntitiesStatus(): void {
            useEntityManagementStore().clearAddEntitiesStatus({ rpName: this.rpName });
            useEntityManagementStore().clearUpdateEntitiesStatus({ rpName: this.rpName });
            useEntityManagementStore().clearPatchEntitiesStatus({ rpName: this.rpName });
        }

        private changeFilters(filters: EntityFilters): void {
            const { customPropertyFilters, collateralFieldFilters, ...standardFilters } = filters;
            const sort = this.$route.query?.sort || undefined;
            this.$router.push({
                ...this.$route,
                query: pickBy({
                    sort,
                    ...standardFilters,
                    ...customPropertyFilters,
                    ...collateralFieldFilters,
                }, Boolean),
            } as any);
        }

        private closeFilters(filters: string[]) {
            this.$router.push({
                ...this.$route,
                query: {
                    ...omit(this.$route.query, filters),
                },
            } as any);
        }

        private onSelectCategory({ changed, value }: { changed: boolean, value: EntityFilterCategory[] }) {
            const searchFilterOnly = Object.keys(this.filters).length === 1 && this.filters.hasOwnProperty('search');
            const noFilters = Object.keys(this.filters).length === 0;
            if (noFilters || searchFilterOnly || getCategoryByFilters(this.filters)) {
                this.changeCategory({ changed, value });
            } else {
                this.$procedures.execute('confirmFilterOverride', {}, this.onConfirmFilterFinish({ changed, value }));
            }
        }

        private onConfirmFilterFinish({ changed, value }: { changed: boolean, value: EntityFilterCategory[] }): any {
            return (modified: boolean) => {
                if (modified) {
                    this.changeCategory({ changed, value });
                }
            };
        }

        private changeCategory({ changed, value }: { changed: boolean, value: EntityFilterCategory[] }): void {
            const sort = this.$route.query?.sort || undefined;
            const search = this.$route.query?.search || undefined;
            const filtersObj = changed ? categoryFilterMap[value[0]] : {};
            this.$router.push({
                ...this.$route,
                query: pickBy({ sort, search, ...filtersObj }, Boolean),
            } as any);
        }

        private async setEntitiesPauseState(
            { entities, isBulk, value }: { entities: Entity[], isBulk: boolean, value: boolean },
        ): Promise<void> {
            await useEntityManagementStore().patchEntities({
                rpName: this.rpName,
                isBulk,
                patch: entities.map((entity) => ({
                    id: entity.id,
                    paused: {
                        op: PatchOperationType.replace,
                        newValue: value,
                    },
                }) as EntityPatch),
            });
            this.createEntitiesActionAlert(this.patchEntitiesStatus, entities, value ? 'paused' : 'unpaused');
        }

        private setEntitiesActiveState(
            { entities, isBulk, value }: { entities: Entity[], isBulk: boolean, value: boolean },
        ): void {
            this.$procedures.execute(value ? 'openActivateEntityProcedure' : 'openDeactivateEntityProcedure', {
                rpName: this.rpName,
                entities,
                isBulk,
            }, (modified: boolean, modifiedEntities: Entity[]) => (value
                ? this.finishActivateEntityProcedure(modified, modifiedEntities)
                : this.finishDeactivateEntityProcedure(modified, modifiedEntities)));
        }

        private clearFilters(): void {
            this.changeFilters({});
        }

        private createEntitiesActionAlert(
            status: PatchEntitiesStatus,
            entities: Entity[],
            operation: string,
        ): void {
            if (status.totalCount > 1) {
                this.createBulkEntitiesActionAlert(status, operation);
            } else {
                this.createSingleEntityActionAlert(status, entities[0], operation);
            }
        }

        private createSingleEntityActionAlert(
            status: PatchEntitiesStatus,
            entity: Entity,
            operation: string,
        ): void {
            const showAlertFunc = this.selectedEntity && entity.id === this.selectedEntity.id
                ? this.showAlertInEntityDetails
                : this.showAlert;
            if (status.status === OperationStatus.success) {
                // TODO: revisit to see if successCount has to be 1 here
                if (status.successCount === 1) {
                    showAlertFunc('success', `${entity.contactEmail} was ${operation}`);
                } else {
                    showAlertFunc('danger', `Something went wrong! ${entity.contactEmail} was not ${operation}`);
                }
            } else if (status.status === OperationStatus.error) {
                showAlertFunc(
                    'danger',
                    `Something went wrong! ${entity.contactEmail} was not ${operation}`,
                );
            }
        }

        private createBulkEntitiesActionAlert(
            status: PatchEntitiesStatus,
            operation: string,
        ): void {
            if (status.status === OperationStatus.success) {
                if (status.successCount === 0) {
                    this.showAlert(
                        'danger',
                        `Something went wrong! ${status.failureCount} entities were not ${operation}`,
                    );
                } else {
                    if (status.successCount === 1) {
                        this.showAlert('success', `${status.successCount} entity was ${operation}`);
                    } else {
                        this.showAlert('success', `${status.successCount} entities were ${operation}`);
                    }
                    if (status.failureCount > 0) {
                        if (status.failureCount === 1) {
                            this.showAdditionalAlert('highWarning', `Something went wrong! ${status.failureCount} entity was not ${operation}`);
                        } else {
                            this.showAdditionalAlert('highWarning', `Something went wrong! ${status.failureCount} entities were not ${operation}`);
                        }
                    }
                }
            } else if (status.status === OperationStatus.error) {
                this.showAlert(
                    'danger',
                    `Something went wrong! ${status.failureCount} entities were not ${operation}`,
                );
            }
        }

        private async onActionsButtonClicked(entityId: string): Promise<void> {
            const entity = this.entitiesStatus.list.find((entity) => entity.id === entityId);
            if (!entity?.active) {
                return;
            }

            if (!this.submissionLinkPerEntityId.hasOwnProperty(entityId)) {
                this.loadingSubmissionLink = true;
                await useDashboardStore().loadEntitySubmissionLink({
                    rpName: this.rpName,
                    entityId,
                });
                this.loadingSubmissionLink = false;
            }
        }

        private copySubmissionLink(submissionLink: string): void {
            navigator.clipboard.writeText(submissionLink);
            useSnackbarStore().displaySnackbar({
                success: true,
                message: `Submission link has been copied to clipboard.`,
            });
        }

        private goToCriterion(riskProfileId: string, requirementType: TprmRequirementType): void {
            const routeData = this.$router.resolve({
                name: 'decisioningRiskProfile',
                params: {
                    rpId: this.$route.params.rpId,
                    id: riskProfileId,
                },
                query: { requirementType },
            });
            window.open(routeData.href, '_blank');
        }

        // PRODUCT-26110 Phase 2B2: prototype method, not an arrow field (see
        // finishDeactivateEntityProcedure). Call site wraps in an arrow.
        private finishGrantExceptionProcedure(modified: boolean, insuredName: string): void {
            if (!modified) {
                return;
            }
            if (this.grantExceptionStatus === OperationStatus.success) {
                this.showAlert('success', `Exception granted for ${insuredName}`);
            } else {
                this.showAlert('danger', `Failed to grant exception for ${insuredName}`);
            }
        }

        private grantException(entity: Entity): void {
            this.$procedures.execute('grantExceptionProcedure', {
                exception: {
                    rpName: this.rpName,
                    insured: entity,
                    data: [ createEntityLevelException() ],
                },
            }, (modified: boolean, insuredName: string) =>
                this.finishGrantExceptionProcedure(modified, insuredName));
        }

        private addNote(entity: Entity): void {
            this.$procedures.execute('addNoteProcedure', {
                rpName: this.rpName,
                entityId: entity.id,
                entityDisplayName: entity.displayName,
            });
        }

        // PRODUCT-26110 Phase 2B2: prototype method, not an arrow field (see
        // finishDeactivateEntityProcedure). Call site wraps in an arrow.
        private finishRemoveExceptionProcedure(modified: boolean, insuredName: string): void {
            if (!modified) {
                return;
            }
            if (this.removeExceptionStatus === OperationStatus.success) {
                this.showAlert('success', `Exception removed for ${insuredName}`);
            } else {
                this.showAlert('danger', `Failed to remove exception for ${insuredName}`);
            }
        }

        private removeExceptions(entity: Entity, exceptionIds: string[]): void {
            this.$procedures.execute('removeExceptionProcedure', {
                rpName: this.rpName,
                entity,
                exceptionIds,
            }, (modified: boolean, insuredName: string) =>
                this.finishRemoveExceptionProcedure(modified, insuredName));
        }

        private onViewSubmissionHistory(entity: Entity): void {
            this.$router.push({
                ...this.$route,
                query: {
                    ...this.$route.query,
                    entity: entity.id,
                    tab: ENTITY_DETAILS_TABS.EVENTS,
                    activityType: SUBMISSION_HISTORY_ACTIVITY_TYPES.join(','),
                },
            } as any).catch(noop);
        }

        private resetAlertConfig(configName: AlertConfigName, timeoutName: AlertTimeoutName): void {
            clearTimeout(this[timeoutName]);
            this[configName] = null;
        }

        private setupAlertConfig(
            type: AlertType,
            title: string,
            configName: AlertConfigName,
            timeoutName: AlertTimeoutName,
        ): void {
            this.resetAlertConfig(configName, timeoutName);
            this[configName] = { type, title };
            this[timeoutName] = setTimeout(() => this.resetAlertConfig(configName, timeoutName), 5000);
        }

        private hideAlert(): void {
            this.resetAlertConfig('alertConfig', 'alertConfigTimeout');
        }

        private showAlert(type: AlertType, title: string): void {
            this.setupAlertConfig(type, title, 'alertConfig', 'alertConfigTimeout');
        }

        private showAlertInEntityDetails(type: AlertType, title: string): void {
            this.setupAlertConfig(type, title, 'entityDetailsAlertConfig', 'entityDetailsAlertTimeout');
        }

        private showAdditionalAlert(type: AlertType, title: string): void {
            this.setupAlertConfig(type, title, 'additionalAlertConfig', 'additionalAlertConfigTimeout');
        }

        // Shared with SendDocumentRequestProcedure, so the create-link modal names a requirement
        // exactly as the entity details panel behind it does.
        private requirementModelsForEntity(entity: Entity): TprmRequirementModel[] {
            return getRequirementModelsWithCountryLabel(
                entity,
                this.requirementModelsStatus.list,
                this.riskProfileStatus.list,
                this.categorizedEnumLabels,
            );
        }

        private unmounted(): void {
            useDashboardStore().clearEntitiesList({ rpName: this.lastRpName });
            useDashboardStore().clearClientRequirementTypeList({ rpName: this.lastRpName });
            useDashboardStore().clearEntityStatistics({ rpName: this.lastRpName });
            useDashboardStore().clearRpConfig({ rpName: this.lastRpName });
            useDashboardConfigurationStore().clearUpdateDisplayStatus({ rpName: this.lastRpName });
            useEntityDetailsStore().clearRequirementDetailsList({ rpName: this.lastRpName });
            useEntityDetailsStore().clearCollateralEntities({ rpName: this.lastRpName });
            useEntityDetailsStore().clearOpenVerification({ rpName: this.lastRpName });
            useContractUploadStore().clearUploadContractStatus({ rpName: this.lastRpName });
            clearTimeout(this.alertConfigTimeout);
            clearTimeout(this.errorAlertTimeout);
            clearTimeout(this.additionalAlertConfigTimeout);
            clearTimeout(this.collectEvidenceAlertTimeout);
            clearTimeout(this.entityDetailsAlertTimeout);
        }
    }
</script>
